import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Chip,
  Divider,
  LinearProgress,
  Autocomplete,
  CircularProgress,
  useTheme,
  keyframes,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  DragIndicator as DragIndicatorIcon,
  Stop as StopIcon,
  MoreVert as MoreVertIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import api from '../services/api';
import type {
  RunbookListItem,
  Runbook,
  RunbookCreateRequest,
  RunbookUpdateRequest,
  RunbookScenarioDetail,
  SlackChannel,
} from '../types/runbooks';
import type { Scenario, Execution } from '../types/attacks';
import { ExecutionStatusColors } from '../types/attacks';
import { usePermissions } from '../contexts/PermissionContext';
import { useTenant } from '../contexts/TenantContext';
import { useNotification } from '../contexts/NotificationContext';
import { PermissionGate } from '../components/PermissionGate';
import { PageTransition } from '../components/PageTransition';
import CreateScenarioDialog from '../components/operations/CreateScenarioDialog';
import ScenarioDebugDialog from '../components/operations/ScenarioDebugDialog';
import ScenarioRowContent, { ATTACK_TYPE_CHIP_PADDING_CH } from '../components/operations/ScenarioRowContent';
import { useTimezone } from '../contexts/TimezoneContext';
import { formatTimeframeRange, formatUiDateOnly, formatUiDateTime, formatUiTimeWithSeconds } from '../utils/dateUtils';
import { cloneScenarioConfiguration, generateDuplicateScenarioName } from '../utils/scenarioDuplication';
import RunbookExecutionDetail from '../components/runbooks/RunbookExecutionDetail';
import { useResetOnTenantChange } from '../hooks/useResetOnTenantChange';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Runbooks: React.FC = () => {
  const theme = useTheme();
  const { timezone } = useTimezone();
  const { hasPermission, isAdmin } = usePermissions();
  const { selectedTenant } = useTenant();
  const { showNotification } = useNotification();
  const canManage = isAdmin || (selectedTenant?.tenant_id ? hasPermission('manage_runbooks', 'tenant', selectedTenant.tenant_id) : false);
  const canActivate = isAdmin || (selectedTenant?.tenant_id ? hasPermission('access_operations', 'tenant', selectedTenant.tenant_id) : false);

  const [runbooks, setRunbooks] = useState<RunbookListItem[]>([]);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(true);
  const setSnackbar = useCallback((value: { open: boolean; message: string; severity: 'success' | 'error' }) => {
    if (value.open) {
      showNotification(value.message, value.severity);
    }
  }, [showNotification]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRunbook, setEditingRunbook] = useState<Runbook | null>(null);
  const [form, setForm] = useState<{
    name: string;
    description: string;
    timeframe_start: string;
    timeframe_end: string;
    max_duration_minutes: string;
    slack_channel: string;
    scenario_ids: string[];
  }>({
    name: '',
    description: '',
    timeframe_start: '',
    timeframe_end: '',
    max_duration_minutes: '',
    slack_channel: '',
    scenario_ids: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([]);
  const [slackConfigured, setSlackConfigured] = useState<boolean | null>(null);
  const [slackChannelsLoaded, setSlackChannelsLoaded] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [runbookToDelete, setRunbookToDelete] = useState<RunbookListItem | Runbook | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [createScenarioOpen, setCreateScenarioOpen] = useState(false);
  const [editingScenario, setEditingScenario] = useState<Scenario | null>(null);
  const [createScenarioContext, setCreateScenarioContext] = useState<'addRemoveDialog' | 'runbookCreate' | null>(null);

  const [detailRunbookId, setDetailRunbookId] = useState<string | null>(null);
  const [detailRunbook, setDetailRunbook] = useState<Runbook | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentScenario, setCurrentScenario] = useState<Scenario | null>(null);
  const [addScenariosOpen, setAddScenariosOpen] = useState(false);
  const [addRemoveScenarioIds, setAddRemoveScenarioIds] = useState<string[]>([]);
  const [addPickerSelection, setAddPickerSelection] = useState<string[]>([]);
  const [addingScenarios, setAddingScenarios] = useState(false);
  const [scenarioReorderLoading, setScenarioReorderLoading] = useState(false);
  const [draggingScenarioId, setDraggingScenarioId] = useState<string | null>(null);
  const [dragOverScenarioId, setDragOverScenarioId] = useState<string | null>(null);
  const [dragOverScenarioPosition, setDragOverScenarioPosition] = useState<'before' | 'after' | null>(null);
  const [activatingScenarioId, setActivatingScenarioId] = useState<string | null>(null);
  const [activateConfirm, setActivateConfirm] = useState<{ scenarioId: string; scenarioName: string } | null>(null);
  /** scenarioId -> true if all targets are in allow list (approved); false if not; undefined while loading or no targets to check */
  const [scenarioAllowListOk, setScenarioAllowListOk] = useState<Record<string, boolean>>({});
  /** scenarioId -> list of target strings that are not in the allow list */
  const [scenarioInvalidTargets, setScenarioInvalidTargets] = useState<Record<string, string[]>>({});
  const [scenarioMenuAnchor, setScenarioMenuAnchor] = useState<null | HTMLElement>(null);
  const [scenarioMenuScenario, setScenarioMenuScenario] = useState<{ id: string; name: string } | null>(null);
  // Scenario "Run Debug" (single ephemeral bot + in-UI terminal) opened from the runbook menu.
  const [debugScenario, setDebugScenario] = useState<{ id: string; name: string } | null>(null);
  const [reordering, setReordering] = useState(false);
  const [draggingRunbookId, setDraggingRunbookId] = useState<string | null>(null);
  const [dragOverRunbookId, setDragOverRunbookId] = useState<string | null>(null);
  const [dragOverRunbookPosition, setDragOverRunbookPosition] = useState<'before' | 'after' | null>(null);

  // Scenario expansion: scenarioId -> expanded bool
  const [expandedScenarioIds, setExpandedScenarioIds] = useState<Record<string, boolean>>({});
  // Scenario executions cache: scenarioId -> executions
  const [scenarioExecutions, setScenarioExecutions] = useState<Record<string, Execution[]>>({});
  const [scenarioExecutionsLoading, setScenarioExecutionsLoading] = useState<Record<string, boolean>>({});
  // Execution detail view: if set we show the full execution detail panel
  const [selectedExecution, setSelectedExecution] = useState<{ execution: Execution; scenarioName: string } | null>(null);

  // Tenant switch reset (CAO-173): all of the state above is scoped to the
  // selected tenant, but only the list fetches below are keyed on it. Without
  // an explicit reset, switching tenants leaves the previous tenant's runbook
  // rendered in the detail overlay — and its scenarios, executions and dialogs
  // cached — under the new tenant.
  const activeTenantIdRef = useRef<string | null>(selectedTenant?.tenant_id ?? null);

  const resetTenantScopedState = useCallback(() => {
    activeTenantIdRef.current = selectedTenant?.tenant_id ?? null;
    // Detail overlay and execution detail
    setDetailRunbookId(null);
    setDetailRunbook(null);
    setDetailLoading(false);
    setSelectedExecution(null);
    setCurrentScenario(null);
    // Per-runbook scenario state
    setExpandedScenarioIds({});
    setScenarioExecutions({});
    setScenarioExecutionsLoading({});
    setScenarioAllowListOk({});
    setScenarioInvalidTargets({});
    setAddScenariosOpen(false);
    setAddRemoveScenarioIds([]);
    setAddPickerSelection([]);
    setScenarioMenuAnchor(null);
    setScenarioMenuScenario(null);
    setActivateConfirm(null);
    setDebugScenario(null);
    // Dialogs still holding the previous tenant's records
    setDialogOpen(false);
    setEditingRunbook(null);
    setDeleteDialogOpen(false);
    setRunbookToDelete(null);
    setCreateScenarioOpen(false);
    setEditingScenario(null);
    setCreateScenarioContext(null);
    // The lists are refetched for the new tenant by the effects below; drop
    // them so the previous tenant's rows never render under the new one.
    setRunbooks([]);
    setScenarios([]);
  }, [selectedTenant?.tenant_id]);

  useResetOnTenantChange(selectedTenant?.tenant_id ?? null, resetTenantScopedState);

  const fetchRunbooks = useCallback(async () => {
    if (!selectedTenant?.tenant_id) return;
    setLoading(true);
    try {
      const data = await api.getRunbooks();
      setRunbooks(data);
    } catch (err) {
      console.error('Error fetching runbooks:', err);
      setSnackbar({ open: true, message: 'Failed to load runbooks', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedTenant?.tenant_id, setSnackbar]);

  const fetchScenarios = useCallback(async () => {
    if (!selectedTenant?.tenant_id) return;
    try {
      const data = await api.getScenarios();
      setScenarios(data);
    } catch (err) {
      console.error('Error fetching scenarios:', err);
    }
  }, [selectedTenant?.tenant_id]);

  useEffect(() => {
    fetchRunbooks();
  }, [fetchRunbooks]);

  useEffect(() => {
    if (!detailRunbook?.scenarios?.length || !selectedTenant?.tenant_id || !canActivate) {
      setScenarioAllowListOk({});
      setScenarioInvalidTargets({});
      return;
    }
    const scenarios = detailRunbook.scenarios;
    const allTargets = Array.from(new Set(scenarios.flatMap((s) => ('targets' in s && Array.isArray(s.targets) ? s.targets : []))));
    if (allTargets.length === 0) {
      const ok: Record<string, boolean> = {};
      const invalid: Record<string, string[]> = {};
      scenarios.forEach((s) => {
        ok[s.id] = true;
        invalid[s.id] = [];
      });
      setScenarioAllowListOk(ok);
      setScenarioInvalidTargets(invalid);
      return;
    }
    let cancelled = false;
    api
      .validateTargetsInAllowList(selectedTenant.tenant_id, allTargets)
      .then((res) => {
        if (cancelled) return;
        const approvedByTarget = new Map<string, boolean>();
        res.validation_results.forEach((r: { target: string; is_approved: boolean }) => {
          approvedByTarget.set(r.target, r.is_approved);
        });
        const ok: Record<string, boolean> = {};
        const invalid: Record<string, string[]> = {};
        scenarios.forEach((s) => {
          const detail = s as RunbookScenarioDetail;
          const targets = 'targets' in detail && Array.isArray(detail.targets) ? detail.targets : [];
          const notApproved = targets.filter((t) => approvedByTarget.get(t) !== true);
          invalid[s.id] = notApproved;
          ok[s.id] = targets.length === 0 || notApproved.length === 0;
        });
        setScenarioAllowListOk(ok);
        setScenarioInvalidTargets(invalid);
      })
      .catch(() => {
        if (!cancelled) {
          setScenarioAllowListOk({});
          setScenarioInvalidTargets({});
        }
      });
    return () => { cancelled = true; };
  }, [detailRunbook?.id, detailRunbook?.scenarios, selectedTenant?.tenant_id, canActivate]);

  const ensureSlackChannelsLoaded = useCallback(async () => {
    if (!isAdmin || slackChannelsLoaded) return;
    try {
      const res = await api.getSlackChannels();
      setSlackChannels(res.channels);
      setSlackConfigured(res.configured);
    } catch (err) {
      console.error('Error fetching Slack channels:', err);
      setSlackConfigured(false);
    } finally {
      setSlackChannelsLoaded(true);
    }
  }, [isAdmin, slackChannelsLoaded]);

  const handleOpenCreate = () => {
    setEditingRunbook(null);
    setForm({
      name: '',
      description: '',
      timeframe_start: '',
      timeframe_end: '',
      max_duration_minutes: '',
      slack_channel: '',
      scenario_ids: [],
    });
    setDialogOpen(true);
    fetchScenarios();
    ensureSlackChannelsLoaded();
  };

  const handleOpenDetail = async (runbook: RunbookListItem) => {
    const requestTenantId = selectedTenant?.tenant_id ?? null;
    setDetailRunbookId(runbook.id);
    setDetailRunbook(null);
    setDetailLoading(true);
    try {
      const [full, data] = await Promise.all([api.getRunbook(runbook.id), api.getCurrentScenario()]);
      // The tenant may have been switched while this was in flight; the reset
      // already closed the overlay, so do not re-open it with stale data.
      if (activeTenantIdRef.current !== requestTenantId) return;
      setDetailRunbook(full);
      setCurrentScenario(data?.scenario ?? null);
    } catch (err) {
      console.error('Error loading runbook:', err);
      if (activeTenantIdRef.current !== requestTenantId) return;
      setSnackbar({ open: true, message: 'Failed to load runbook', severity: 'error' });
      setDetailRunbookId(null);
    } finally {
      if (activeTenantIdRef.current === requestTenantId) setDetailLoading(false);
    }
  };

  const handleBackToList = () => {
    setDetailRunbookId(null);
    setDetailRunbook(null);
    setAddScenariosOpen(false);
    fetchRunbooks();
  };

  const handleBackToRunbook = () => {
    setSelectedExecution(null);
  };

  const handleToggleScenarioExpand = async (scenarioId: string, scenarioName: string) => {
    const isExpanded = expandedScenarioIds[scenarioId];
    if (isExpanded) {
      setExpandedScenarioIds((prev) => ({ ...prev, [scenarioId]: false }));
      return;
    }
    setExpandedScenarioIds((prev) => ({ ...prev, [scenarioId]: true }));
    if (scenarioExecutions[scenarioId] !== undefined) return;
    setScenarioExecutionsLoading((prev) => ({ ...prev, [scenarioId]: true }));
    try {
      const execs = await api.getExecutions(scenarioId);
      execs.sort((a, b) => {
        const ta = a.started_at ? new Date(a.started_at).getTime() : 0;
        const tb = b.started_at ? new Date(b.started_at).getTime() : 0;
        return tb - ta;
      });
      setScenarioExecutions((prev) => ({ ...prev, [scenarioId]: execs }));
    } catch (err) {
      console.error('Error loading executions for scenario:', err);
      setScenarioExecutions((prev) => ({ ...prev, [scenarioId]: [] }));
    } finally {
      setScenarioExecutionsLoading((prev) => ({ ...prev, [scenarioId]: false }));
    }
  };

  const handleOpenExecutionDetail = async (executionId: string, scenarioName: string) => {
    const requestTenantId = selectedTenant?.tenant_id ?? null;
    try {
      const exec = await api.getExecution(executionId);
      if (activeTenantIdRef.current !== requestTenantId) return;
      setSelectedExecution({ execution: exec, scenarioName });
    } catch (err) {
      console.error('Error loading execution:', err);
      showNotification('Failed to load execution details', 'error');
    }
  };

  const handleExecutionUpdated = (updated: Execution) => {
    const scenarioId = updated.scenario_id;
    setScenarioExecutions((prev) => {
      const list = prev[scenarioId];
      if (!list) return prev;
      return { ...prev, [scenarioId]: list.map((e) => (e.id === updated.id ? updated : e)) };
    });
    if (selectedExecution?.execution.id === updated.id) {
      setSelectedExecution((prev) => prev ? { ...prev, execution: updated } : prev);
    }
  };

  const getExecutionStatusColor = (status: string) => {
    const key = status as keyof typeof ExecutionStatusColors;
    return (ExecutionStatusColors[key] ?? 'default') as
      | 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  };

  function formatDuration(start?: string, end?: string): string {
    if (!start || !end) return '—';
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (!Number.isFinite(ms) || ms < 0) return '—';
    if (ms < 1000) return `${ms} ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
    const m = Math.floor(ms / 60_000);
    const s = Math.round((ms % 60_000) / 1000);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }

  const handleEditFromDetail = () => {
    if (!detailRunbook) return;
    setEditingRunbook(detailRunbook);
    const scenarioIds = detailRunbook.scenarios.map((s) => s.id);
    setForm({
      name: detailRunbook.name,
      description: detailRunbook.description ?? '',
      timeframe_start: detailRunbook.timeframe_start ? detailRunbook.timeframe_start.slice(0, 16) : '',
      timeframe_end: detailRunbook.timeframe_end ? detailRunbook.timeframe_end.slice(0, 16) : '',
      max_duration_minutes: detailRunbook.max_duration_minutes != null ? String(detailRunbook.max_duration_minutes) : '',
      slack_channel: detailRunbook.slack_channel ?? '',
      scenario_ids: scenarioIds,
    });
    setDialogOpen(true);
    fetchScenarios();
    ensureSlackChannelsLoaded();
  };

  const handleRunbookDrop = async (sourceId: string, dropTargetId: string, position: 'before' | 'after') => {
    if (sourceId === dropTargetId) return;
    const sourceRunbook = runbooks.find((r) => r.id === sourceId);
    if (!sourceRunbook) return;
    const withoutSource = runbooks.filter((r) => r.id !== sourceId);
    const dropIndex = withoutSource.findIndex((r) => r.id === dropTargetId);
    const baseIndex = dropIndex >= 0 ? dropIndex : withoutSource.length;
    const insertIndex = position === 'after' ? baseIndex + 1 : baseIndex;
    const reordered = [...withoutSource];
    reordered.splice(insertIndex, 0, sourceRunbook);
    const newIds = reordered.map((r) => r.id);
    setReordering(true);
    setDraggingRunbookId(null);
    setDragOverRunbookId(null);
    setDragOverRunbookPosition(null);
    try {
      const updated = await api.reorderRunbooks(newIds);
      setRunbooks(updated);
      setSnackbar({ open: true, message: 'Order updated', severity: 'success' });
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to reorder';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
    } finally {
      setReordering(false);
    }
  };

  const handleScenarioDrop = async (sourceId: string, dropTargetId: string, position: 'before' | 'after') => {
    if (!detailRunbook || sourceId === dropTargetId) return;
    const sourceScenario = detailRunbook.scenarios.find((sc) => sc.id === sourceId);
    if (!sourceScenario) return;
    const withoutSource = detailRunbook.scenarios.filter((sc) => sc.id !== sourceId);
    const dropIndex = withoutSource.findIndex((sc) => sc.id === dropTargetId);
    const baseIndex = dropIndex >= 0 ? dropIndex : withoutSource.length;
    const insertIndex = position === 'after' ? baseIndex + 1 : baseIndex;
    const reordered = [...withoutSource];
    reordered.splice(insertIndex, 0, sourceScenario);
    const newIds = reordered.map((s) => s.id);
    setScenarioReorderLoading(true);
    setDraggingScenarioId(null);
    setDragOverScenarioId(null);
    setDragOverScenarioPosition(null);
    try {
      await api.updateRunbook(detailRunbook.id, { scenario_ids: newIds });
      const updated = await api.getRunbook(detailRunbook.id);
      setDetailRunbook(updated);
      setSnackbar({ open: true, message: 'Scenario order updated', severity: 'success' });
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to reorder';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
    } finally {
      setScenarioReorderLoading(false);
    }
  };

  const handleActivateScenario = async (scenarioId: string) => {
    setActivatingScenarioId(scenarioId);
    try {
      await api.setAttackScenario(scenarioId);
      const data = await api.getCurrentScenario();
      setCurrentScenario(data?.scenario ?? null);
      setSnackbar({ open: true, message: 'Scenario activated', severity: 'success' });
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to activate scenario';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
      try {
        const data = await api.getCurrentScenario();
        setCurrentScenario(data?.scenario ?? null);
      } catch {
        setCurrentScenario(null);
      }
    } finally {
      setActivatingScenarioId(null);
    }
  };

  const handleEndScenario = async () => {
    const activeId = currentScenario?.id ?? null;
    if (!activeId) return;
    setActivatingScenarioId(activeId);
    try {
      await api.endScenario();
      const data = await api.getCurrentScenario();
      setCurrentScenario(data?.scenario ?? null);
      setSnackbar({ open: true, message: 'Scenario ended', severity: 'success' });
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to end scenario';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
      try {
        const data = await api.getCurrentScenario();
        setCurrentScenario(data?.scenario ?? null);
      } catch {
        setCurrentScenario(null);
      }
    } finally {
      setActivatingScenarioId(null);
    }
  };

  const onActivateScenarioClick = (scenarioId: string, scenarioName: string) => {
    if (currentScenario && currentScenario.id !== scenarioId) {
      setActivateConfirm({ scenarioId, scenarioName });
      return;
    }
    handleActivateScenario(scenarioId);
  };

  const handleActivateConfirmClose = () => {
    setActivateConfirm(null);
  };

  const handleActivateConfirm = async () => {
    if (!activateConfirm) return;
    const { scenarioId, scenarioName } = activateConfirm;
    setActivatingScenarioId(scenarioId);
    setActivateConfirm(null);
    try {
      await api.endScenario();
      await api.setAttackScenario(scenarioId);
      const data = await api.getCurrentScenario();
      setCurrentScenario(data?.scenario ?? null);
      setSnackbar({ open: true, message: `Terminated previous scenario and activated "${scenarioName}"`, severity: 'success' });
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to terminate and activate scenario';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
      try {
        const data = await api.getCurrentScenario();
        setCurrentScenario(data?.scenario ?? null);
      } catch {
        setCurrentScenario(null);
      }
    } finally {
      setActivatingScenarioId(null);
    }
  };

  const handleApplyAddRemoveScenarios = async () => {
    if (!detailRunbook) return;
    setAddingScenarios(true);
    try {
      await api.updateRunbook(detailRunbook.id, { scenario_ids: addRemoveScenarioIds });
      const updated = await api.getRunbook(detailRunbook.id);
      setDetailRunbook(updated);
      setAddScenariosOpen(false);
      setSnackbar({ open: true, message: 'Scenarios updated', severity: 'success' });
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to update scenarios';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
    } finally {
      setAddingScenarios(false);
    }
  };

  const handleRemoveScenarioFromRunbook = async (scenarioId: string) => {
    if (!detailRunbook) return;
    const newIds = detailRunbook.scenarios.map((s) => s.id).filter((id) => id !== scenarioId);
    try {
      await api.updateRunbook(detailRunbook.id, { scenario_ids: newIds });
      const updated = await api.getRunbook(detailRunbook.id);
      setDetailRunbook(updated);
      handleScenarioMenuClose();
      setSnackbar({ open: true, message: 'Scenario removed from runbook', severity: 'success' });
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to remove scenario from runbook';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRunbook(null);
  };

  const handleSubmit = async () => {
    const name = form.name.trim();
    if (!name) {
      setSnackbar({ open: true, message: 'Name is required', severity: 'error' });
      return;
    }
    const start = form.timeframe_start ? new Date(form.timeframe_start).toISOString() : undefined;
    const end = form.timeframe_end ? new Date(form.timeframe_end).toISOString() : undefined;
    if (start && end && new Date(end) <= new Date(start)) {
      setSnackbar({ open: true, message: 'End time must be after start time', severity: 'error' });
      return;
    }
    const maxDur = form.max_duration_minutes.trim();
    const max_duration_minutes = maxDur ? parseInt(maxDur, 10) : undefined;
    if (max_duration_minutes !== undefined && (isNaN(max_duration_minutes) || max_duration_minutes <= 0)) {
      setSnackbar({ open: true, message: 'Max duration must be a positive number', severity: 'error' });
      return;
    }

    // Only super admins can write slack_channel; only include it in payloads when admin
    // AND it actually changed (so non-admins editing other fields don't trip the gate).
    // Send the empty string (not null) to clear — the backend treats "" as "no channel".
    const trimmedChannel = form.slack_channel.trim();
    const channelChanged = isAdmin && (editingRunbook
      ? trimmedChannel !== (editingRunbook.slack_channel ?? '')
      : trimmedChannel.length > 0);
    const channelPayload = channelChanged ? trimmedChannel : undefined;

    setSubmitting(true);
    try {
      if (editingRunbook) {
        const payload: RunbookUpdateRequest = {
          name,
          description: form.description.trim() || undefined,
          timeframe_start: start,
          timeframe_end: end,
          max_duration_minutes,
          scenario_ids: form.scenario_ids,
          ...(channelPayload !== undefined ? { slack_channel: channelPayload } : {}),
        };
        await api.updateRunbook(editingRunbook.id, payload);
        setSnackbar({ open: true, message: 'Runbook updated', severity: 'success' });
      } else {
        const payload: RunbookCreateRequest = {
          name,
          description: form.description.trim() || undefined,
          timeframe_start: start,
          timeframe_end: end,
          max_duration_minutes,
          scenario_ids: form.scenario_ids.length ? form.scenario_ids : undefined,
          ...(channelPayload !== undefined ? { slack_channel: channelPayload } : {}),
        };
        await api.createRunbook(payload);
        setSnackbar({ open: true, message: 'Runbook created', severity: 'success' });
      }
      handleCloseDialog();
      fetchRunbooks();
      if (editingRunbook && detailRunbookId === editingRunbook.id) {
        const updated = await api.getRunbook(editingRunbook.id);
        setDetailRunbook(updated);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Request failed';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (runbook: RunbookListItem | Runbook) => {
    setRunbookToDelete(runbook);
    setDeleteDialogOpen(true);
  };

  const handleDuplicateRunbook = async (runbook: RunbookListItem | Runbook) => {
    try {
      const copy = await api.duplicateRunbook(runbook.id);
      setSnackbar({ open: true, message: `Runbook "${copy.name}" created`, severity: 'success' });
      fetchRunbooks();
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to duplicate runbook';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!runbookToDelete) return;
    setDeleting(true);
    try {
      await api.deleteRunbook(runbookToDelete.id);
      setSnackbar({ open: true, message: 'Runbook deleted', severity: 'success' });
      setDeleteDialogOpen(false);
      setRunbookToDelete(null);
      if (detailRunbookId === runbookToDelete.id) {
        setDetailRunbookId(null);
        setDetailRunbook(null);
      }
      fetchRunbooks();
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Delete failed';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleScenarioMenuOpen = (event: React.MouseEvent<HTMLElement>, scenario: { id: string; name: string }) => {
    setScenarioMenuAnchor(event.currentTarget);
    setScenarioMenuScenario(scenario);
  };
  const handleScenarioMenuClose = () => {
    setScenarioMenuAnchor(null);
    setScenarioMenuScenario(null);
  };

  const handleEditScenarioClick = async (scenarioId: string) => {
    try {
      const full = await api.getScenario(scenarioId);
      setEditingScenario(full);
      setCreateScenarioContext(null);
      setCreateScenarioOpen(true);
    } catch (err) {
      console.error('Error loading scenario:', err);
      setSnackbar({ open: true, message: 'Failed to load scenario', severity: 'error' });
    }
  };

  const handleDuplicateScenarioClick = async (scenarioId: string) => {
    try {
      const full = await api.getScenario(scenarioId);
      const duplicatedName = generateDuplicateScenarioName(
        full.name,
        scenarios.map((s) => s.name)
      );
      const duplicated = await api.createScenario({
        name: duplicatedName,
        description: full.description ?? undefined,
        attack_type_id: full.attack_type_id,
        configuration: cloneScenarioConfiguration(full.configuration),
      });

      await fetchScenarios();

      if (detailRunbook) {
        const updatedScenarioIds = [
          ...detailRunbook.scenarios.map((s) => s.id),
          duplicated.id,
        ];
        await api.updateRunbook(detailRunbook.id, { scenario_ids: updatedScenarioIds });
        const refreshedRunbook = await api.getRunbook(detailRunbook.id);
        setDetailRunbook(refreshedRunbook);
      }

      setSnackbar({
        open: true,
        message: `Scenario "${duplicatedName}" duplicated`,
        severity: 'success',
      });
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to duplicate scenario';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
    }
  };

  const handleCreateScenarioSuccess = () => {
    if (editingScenario) {
      setEditingScenario(null);
      setCreateScenarioOpen(false);
      if (detailRunbook?.id) {
        api.getRunbook(detailRunbook.id).then(setDetailRunbook);
      }
      fetchScenarios();
      return;
    }
    if (createScenarioContext === 'addRemoveDialog') {
      fetchScenarios().then(() => {
        api.getScenarios().then((list) => {
          if (list.length > 0) {
            const last = list[list.length - 1];
            setAddRemoveScenarioIds((prev) => (prev.includes(last.id) ? prev : [...prev, last.id]));
          }
        });
      });
      setCreateScenarioOpen(false);
      setCreateScenarioContext(null);
      return;
    }
    fetchScenarios().then(() => {
      api.getScenarios().then((list) => {
        if (list.length > 0) {
          const last = list[list.length - 1];
          setForm((prev) => ({
            ...prev,
            scenario_ids: prev.scenario_ids.includes(last.id) ? prev.scenario_ids : [...prev.scenario_ids, last.id],
          }));
        }
      });
    });
    setCreateScenarioOpen(false);
  };

  const selectedScenarios = form.scenario_ids
    .map((id) => scenarios.find((s) => s.id === id))
    .filter(Boolean) as Scenario[];

  return (
    <PermissionGate
      permission="view_runbooks"
      fallback={
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" color="text.secondary" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You need permission to view runbooks.
          </Typography>
        </Box>
      }
    >
      <PageTransition>
        {/* Loading overlay when activating scenario (same as Operations) */}
        {activatingScenarioId && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
              gap: 3,
            }}
          >
            <CircularProgress
              size={80}
              sx={{
                color: theme.palette.primary.main,
                filter: `drop-shadow(0 0 30px ${alpha(theme.palette.primary.main, 0.8)})`,
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: theme.palette.primary.main,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                textShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.6)}`,
              }}
            >
              Activating Scenario...
            </Typography>
            <Box
              sx={{
                width: '300px',
                height: '3px',
                background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                animation: `${fadeIn} 1.5s ease-in-out infinite`,
              }}
            />
          </Box>
        )}
        {/* Full-screen runbook detail view */}
        {detailRunbookId ? (
          <Box sx={{ width: '100%', minHeight: '100%' }}>
            {/* Breadcrumb navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
              <IconButton
                onClick={selectedExecution ? handleBackToRunbook : handleBackToList}
                aria-label={selectedExecution ? 'Back to runbook' : 'Back to runbooks'}
                size="medium"
              >
                <ArrowBackIcon />
              </IconButton>
              {/* Breadcrumb: Runbooks > RunbookName > (ScenarioName > ExecutionDate) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' }, transition: 'color 0.2s' }}
                  onClick={handleBackToList}
                >
                  Runbooks
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ px: 0.25 }}>/</Typography>
                <Typography
                  variant="body2"
                  color={selectedExecution ? 'text.secondary' : 'text.primary'}
                  fontWeight={selectedExecution ? 400 : 600}
                  sx={selectedExecution ? { cursor: 'pointer', '&:hover': { color: 'primary.main' }, transition: 'color 0.2s' } : {}}
                  onClick={selectedExecution ? handleBackToRunbook : undefined}
                >
                  {detailRunbook?.name ?? 'Runbook'}
                </Typography>
                {selectedExecution && (
                  <>
                    <Typography variant="body2" color="text.disabled" sx={{ px: 0.25 }}>/</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' }, transition: 'color 0.2s' }} onClick={handleBackToRunbook}>
                      {selectedExecution.scenarioName}
                    </Typography>
                    <Typography variant="body2" color="text.disabled" sx={{ px: 0.25 }}>/</Typography>
                    <Typography variant="body2" color="text.primary" fontWeight={600}>
                      {selectedExecution.execution.started_at ? formatUiDateTime(selectedExecution.execution.started_at, timezone) : selectedExecution.execution.id.slice(0, 8)}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {detailLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Execution Detail view */}
            {selectedExecution && (
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Execution — {selectedExecution.scenarioName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedExecution.execution.started_at ? formatUiDateTime(selectedExecution.execution.started_at, timezone) : '—'}
                    {selectedExecution.execution.completed_at && ` → ${formatUiDateTime(selectedExecution.execution.completed_at, timezone)}`}
                  </Typography>
                </Box>
                <RunbookExecutionDetail
                  execution={selectedExecution.execution}
                  scenarioName={selectedExecution.scenarioName}
                  onExecutionUpdated={handleExecutionUpdated}
                />
              </Box>
            )}

            {/* Runbook detail with expandable scenarios */}
            {!selectedExecution && !detailLoading && detailRunbook && (
              <Box>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {detailRunbook.name}
                </Typography>
                {detailRunbook.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {detailRunbook.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  <Chip
                    label={`Timeframe: ${formatTimeframeRange(detailRunbook.timeframe_start, detailRunbook.timeframe_end, timezone)}`}
                    size="small"
                    variant="outlined"
                  />
                  {detailRunbook.max_duration_minutes != null && (
                    <Chip
                      label={`Max duration: ${detailRunbook.max_duration_minutes} min`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {canManage && (
                    <Box sx={{ display: 'flex', gap: 1, marginLeft: 'auto' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => { setAddRemoveScenarioIds(detailRunbook?.scenarios.map((s) => s.id) ?? []); setAddPickerSelection([]); setAddScenariosOpen(true); fetchScenarios(); }}
                      >
                        Add/remove scenarios
                      </Button>
                      <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={handleEditFromDetail}>
                        Edit runbook
                      </Button>
                      <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={() => handleDuplicateRunbook(detailRunbook)}>
                        Duplicate
                      </Button>
                      <Button variant="outlined" size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteClick(detailRunbook)}>
                        Delete runbook
                      </Button>
                    </Box>
                  )}
                </Box>

                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Scenarios ({detailRunbook.scenarios.length})
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'hidden', maxWidth: '100%' }}>
                    <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: 640 }}>
                      <TableHead>
                        <TableRow>
                          {canManage && <TableCell sx={{ width: 56, py: 2, px: 1.5 }} />}
                          <TableCell sx={{ py: 2, pl: 3, pr: 6, fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem', fontFamily: '"Space Mono", monospace' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Box sx={{ minWidth: `${(detailRunbook.scenarios.length ? Math.min(Math.max(12, ...detailRunbook.scenarios.map((sc) => ('attack_type_name' in sc ? (sc as RunbookScenarioDetail).attack_type_name.length : 0))), 40) : 12) + ATTACK_TYPE_CHIP_PADDING_CH}ch`, flexShrink: 0 }}>Attack Type</Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>Target/s</Box>
                              <Box sx={{ flexShrink: 0, width: '12ch', minWidth: '12ch' }}>Hosting</Box>
                              <Box sx={{ flexShrink: 0, marginLeft: 'auto' }}>Last Execution</Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 2, pl: 2, pr: 2, fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem', width: 100, minWidth: 100 }}>Impact Bots</TableCell>
                          {(canManage || canActivate) && <TableCell align="right" sx={{ py: 2, pl: 3, pr: 5, fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem', width: 96, minWidth: 96 }}>Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                    <TableBody>
                      {detailRunbook.scenarios.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={(canManage ? 1 : 0) + 1 + 1 + ((canManage || canActivate) ? 1 : 0)}>
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                              No scenarios in this runbook. {canManage && 'Use "Add/remove scenarios" to add some.'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        detailRunbook.scenarios.map((s, index) => {
                          const detail = s as RunbookScenarioDetail;
                          const hasDetail = 'attack_type_name' in detail;
                          const isActive = currentScenario?.id === s.id;
                          const scenarioTargets = hasDetail && Array.isArray(detail.targets) ? detail.targets : [];
                          const attackTypeChipMinWidthCh = Math.min(
                            Math.max(12, ...detailRunbook.scenarios.map((sc) => ('attack_type_name' in sc ? (sc as RunbookScenarioDetail).attack_type_name.length : 0))),
                            40
                          );
                          const showScenarioLineBefore = canManage && draggingScenarioId && dragOverScenarioId === s.id && dragOverScenarioPosition === 'before';
                          const showScenarioLineAfter = canManage && draggingScenarioId && dragOverScenarioId === s.id && dragOverScenarioPosition === 'after';
                          const scenarioDropLineSx = {
                            padding: 0,
                            minHeight: 2,
                            lineHeight: 0,
                            borderTop: '3px solid',
                            borderTopColor: theme.palette.primary.main,
                          };
                          const scenarioDropLineAfterSx = {
                            ...scenarioDropLineSx,
                            borderTop: 'none',
                            borderBottom: '3px solid',
                            borderBottomColor: theme.palette.primary.main,
                          };
                          const setScenarioDropPosition = (e: React.DragEvent, id: string, position?: 'before' | 'after') => {
                            e.preventDefault();
                            if (position != null) {
                              setDragOverScenarioId(id);
                              setDragOverScenarioPosition(position);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              const mid = rect.top + rect.height / 2;
                              setDragOverScenarioId(id);
                              setDragOverScenarioPosition(e.clientY < mid ? 'before' : 'after');
                            }
                          };
                          const hasActions = canManage || canActivate;
                          const colSpan = (canManage ? 1 : 0) + 1 + 1 + (hasActions ? 1 : 0);
                          const isScenarioExpanded = Boolean(expandedScenarioIds[s.id]);
                          const execsForScenario = scenarioExecutions[s.id] ?? [];
                          const execsLoading = Boolean(scenarioExecutionsLoading[s.id]);
                          return (
                            <React.Fragment key={s.id}>
                              {showScenarioLineBefore && (
                                <TableRow key={`drop-before-${s.id}`} onDragOver={(e: React.DragEvent) => setScenarioDropPosition(e, s.id, 'before')} sx={{ '& > td': scenarioDropLineSx }}>
                                  <TableCell colSpan={colSpan} />
                                </TableRow>
                              )}
                            <TableRow
                              key={s.id}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.5) },
                                backgroundColor: isScenarioExpanded ? alpha(theme.palette.primary.main, 0.04) : undefined,
                              }}
                              onClick={() => handleToggleScenarioExpand(s.id, s.name)}
                              {...(canManage && {
                                onDragOver: (e: React.DragEvent) => { e.stopPropagation(); setScenarioDropPosition(e, s.id); },
                                onDragLeave: () => {
                                  if (dragOverScenarioId === s.id) {
                                    setDragOverScenarioId(null);
                                    setDragOverScenarioPosition(null);
                                  }
                                },
                                onDrop: (e: React.DragEvent) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const sourceId = e.dataTransfer.getData('text/plain');
                                  const pos = dragOverScenarioPosition ?? 'before';
                                  if (sourceId) handleScenarioDrop(sourceId, s.id, pos);
                                  setDragOverScenarioId(null);
                                  setDragOverScenarioPosition(null);
                                  setDraggingScenarioId(null);
                                },
                              })}
                            >
                              {canManage && (
                                <TableCell onClick={(e) => e.stopPropagation()} sx={{ cursor: 'default', width: 56, px: 1.5, py: 2, verticalAlign: 'middle' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" color="text.disabled" sx={{ minWidth: 16, textAlign: 'right' }}>{index + 1}</Typography>
                                    <Box
                                      component="span"
                                      draggable
                                      onDragStart={(e: React.DragEvent) => {
                                        e.stopPropagation();
                                        e.dataTransfer.setData('text/plain', s.id);
                                        e.dataTransfer.effectAllowed = 'move';
                                        setDraggingScenarioId(s.id);
                                        setDragOverScenarioId(null);
                                        setDragOverScenarioPosition(null);
                                      }}
                                      onDragEnd={() => {
                                        setDraggingScenarioId(null);
                                        setDragOverScenarioId(null);
                                        setDragOverScenarioPosition(null);
                                      }}
                                      sx={{
                                        cursor: scenarioReorderLoading ? 'default' : draggingScenarioId === s.id ? 'grabbing' : 'grab',
                                        opacity: scenarioReorderLoading ? 0.5 : 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 24,
                                        height: 24,
                                        borderRadius: 1,
                                        color: theme.palette.primary.main,
                                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                                      }}
                                      title="Drag to reorder"
                                      aria-label="Drag to reorder scenario"
                                    >
                                      <DragIndicatorIcon sx={{ fontSize: '1.1rem' }} />
                                    </Box>
                                  </Box>
                                </TableCell>
                              )}

                              <TableCell sx={{ py: 2, px: 3, pr: 6, overflow: 'hidden', width: '15ch'}}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <ScenarioRowContent
                                      scenarioName={s.name}
                                      isActive={isActive}
                                      targetsNotInAllowList={scenarioInvalidTargets[s.id] ?? []}
                                      attackTypeName={hasDetail ? detail.attack_type_name : ''}
                                      attackClass={hasDetail ? detail.attack_class : ''}
                                      attackTypeDescription={hasDetail ? detail.attack_type_description : undefined}
                                      dateLabel={hasDetail && detail.last_execution_at ? formatUiDateOnly(detail.last_execution_at, timezone) : '—'}
                                      timeLabel={hasDetail && detail.last_execution_at ? formatUiTimeWithSeconds(detail.last_execution_at, timezone) : undefined}
                                      targets={scenarioTargets}
                                      rowId={s.id}
                                      attackTypeChipMinWidthCh={attackTypeChipMinWidthCh}
                                      hostingProvider={hasDetail ? detail.hosting_provider ?? undefined : undefined}
                                    />
                                  </Box>
                                  <Tooltip title={isScenarioExpanded ? 'Collapse executions' : 'Expand executions'} arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); handleToggleScenarioExpand(s.id, s.name); }}
                                      aria-label={isScenarioExpanded ? 'Collapse executions' : 'Expand executions'}
                                      sx={{ ml: 0.5, flexShrink: 0, color: isScenarioExpanded ? 'primary.main' : 'text.secondary' }}
                                    >
                                      {isScenarioExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ py: 2, pl: 2, pr: 2, width: 100, minWidth: 100, verticalAlign: 'middle', fontFamily: '"Space Mono", monospace', fontSize: '0.875rem' }}>
                                {hasDetail && detail.last_execution_impact_bots != null
                                  ? String(detail.last_execution_impact_bots)
                                  : '—'}
                              </TableCell>
                              {hasActions && (
                                <TableCell align="right" sx={{ width: 96, minWidth: 96, py: 2, pl: 3, pr: 5, verticalAlign: 'middle' }}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); handleScenarioMenuOpen(e, { id: s.id, name: s.name }); }}
                                    aria-label="Scenario actions"
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                            {/* Expandable executions sub-rows */}
                            {isScenarioExpanded && (
                              <TableRow key={`exec-expand-${s.id}`} sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5) }}>
                                <TableCell colSpan={colSpan} sx={{ p: 0, border: 0, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                  <Collapse in={isScenarioExpanded} timeout="auto">
                                    <Box sx={{ px: 2, py: 1.5 }}>
                                      {execsLoading ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                                          <CircularProgress size={18} />
                                          <Typography variant="body2" color="text.secondary">Loading executions…</Typography>
                                        </Box>
                                      ) : execsForScenario.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                          No executions for this scenario yet.
                                        </Typography>
                                      ) : (
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', mb: 1 }}>
                                            Executions ({execsForScenario.length})
                                          </Typography>
                                          <TableContainer sx={{ maxHeight: 240 }}>
                                            <Table size="small" stickyHeader>
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', py: 0.75, backgroundColor: alpha(theme.palette.background.paper, 0.95) }}>Started</TableCell>
                                                  <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', py: 0.75, backgroundColor: alpha(theme.palette.background.paper, 0.95) }}>Completed</TableCell>
                                                  <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', py: 0.75, backgroundColor: alpha(theme.palette.background.paper, 0.95) }}>Duration</TableCell>
                                                  <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', py: 0.75, backgroundColor: alpha(theme.palette.background.paper, 0.95) }}>Status</TableCell>
                                                  <TableCell align="right" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', py: 0.75, backgroundColor: alpha(theme.palette.background.paper, 0.95) }}>Impact Bots</TableCell>
                                                  <TableCell sx={{ width: 48, backgroundColor: alpha(theme.palette.background.paper, 0.95) }} />
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {execsForScenario.map((ex) => (
                                                  <TableRow
                                                    key={ex.id}
                                                    hover
                                                    sx={{
                                                      cursor: 'pointer',
                                                      '&:hover .open-exec-btn': { opacity: 1 },
                                                    }}
                                                    onClick={(e) => { e.stopPropagation(); handleOpenExecutionDetail(ex.id, s.name); }}
                                                  >
                                                    <TableCell sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.8rem', py: 0.75 }}>
                                                      {ex.started_at ? (
                                                        <Box>
                                                          <Typography variant="body2" sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.8rem' }}>
                                                            {formatUiDateOnly(ex.started_at, timezone)}
                                                          </Typography>
                                                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"Space Mono", monospace' }}>
                                                            {formatUiTimeWithSeconds(ex.started_at, timezone)}
                                                          </Typography>
                                                        </Box>
                                                      ) : '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.8rem', py: 0.75 }}>
                                                      {ex.completed_at ? (
                                                        <Box>
                                                          <Typography variant="body2" sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.8rem' }}>
                                                            {formatUiDateOnly(ex.completed_at, timezone)}
                                                          </Typography>
                                                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"Space Mono", monospace' }}>
                                                            {formatUiTimeWithSeconds(ex.completed_at, timezone)}
                                                          </Typography>
                                                        </Box>
                                                      ) : '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.8rem', py: 0.75 }}>
                                                      {formatDuration(ex.started_at, ex.completed_at)}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.75 }}>
                                                      <Chip
                                                        label={ex.status}
                                                        size="small"
                                                        color={getExecutionStatusColor(ex.status)}
                                                        variant="filled"
                                                      />
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.8rem', py: 0.75 }}>
                                                      {ex.impact_bots ?? '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.75, textAlign: 'center' }}>
                                                      <Tooltip title="View timeline & metrics" arrow>
                                                        <IconButton
                                                          size="small"
                                                          className="open-exec-btn"
                                                          onClick={(e) => { e.stopPropagation(); handleOpenExecutionDetail(ex.id, s.name); }}
                                                          aria-label="View execution details"
                                                          sx={{ opacity: 0, transition: 'opacity 0.15s', color: 'primary.main' }}
                                                        >
                                                          <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                      </Tooltip>
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </TableContainer>
                                        </Box>
                                      )}
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            )}
                              {showScenarioLineAfter && (
                                <TableRow key={`drop-after-${s.id}`} onDragOver={(e: React.DragEvent) => setScenarioDropPosition(e, s.id, 'after')} sx={{ '& > td': scenarioDropLineAfterSx }}>
                                  <TableCell colSpan={colSpan} />
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>

                <Menu
                  anchorEl={scenarioMenuAnchor}
                  open={Boolean(scenarioMenuAnchor)}
                  onClose={handleScenarioMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {canManage && scenarioMenuScenario && (
                    <MenuItem
                      onClick={() => {
                        handleEditScenarioClick(scenarioMenuScenario.id);
                        handleScenarioMenuClose();
                      }}
                    >
                      <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>Edit</ListItemText>
                    </MenuItem>
                  )}
                  {canManage && scenarioMenuScenario && (
                    <MenuItem
                      onClick={() => {
                        handleDuplicateScenarioClick(scenarioMenuScenario.id);
                        handleScenarioMenuClose();
                      }}
                    >
                      <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>Duplicate</ListItemText>
                    </MenuItem>
                  )}
                  {canActivate && scenarioMenuScenario && (
                    <>
                      {currentScenario?.id === scenarioMenuScenario.id ? (
                        <MenuItem
                          disabled={activatingScenarioId === scenarioMenuScenario.id}
                          onClick={() => {
                            handleEndScenario();
                            handleScenarioMenuClose();
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          <ListItemIcon><StopIcon fontSize="small" color="error" /></ListItemIcon>
                          <ListItemText>End scenario</ListItemText>
                        </MenuItem>
                      ) : (
                        <MenuItem
                          disabled={
                            activatingScenarioId === scenarioMenuScenario.id ||
                            scenarioAllowListOk[scenarioMenuScenario.id] === false
                          }
                          onClick={() => {
                            onActivateScenarioClick(scenarioMenuScenario.id, scenarioMenuScenario.name);
                            handleScenarioMenuClose();
                          }}
                        >
                          <ListItemIcon><PlayArrowIcon fontSize="small" /></ListItemIcon>
                          <ListItemText>Activate</ListItemText>
                        </MenuItem>
                      )}
                    </>
                  )}
                  {canActivate && scenarioMenuScenario && (
                    <MenuItem
                      disabled={
                        Boolean(currentScenario) ||
                        scenarioAllowListOk[scenarioMenuScenario.id] === false
                      }
                      onClick={() => {
                        setDebugScenario(scenarioMenuScenario);
                        handleScenarioMenuClose();
                      }}
                    >
                      <ListItemIcon><BugReportIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>Run Debug</ListItemText>
                    </MenuItem>
                  )}
                  {canManage && scenarioMenuScenario && (
                    <>
                      <Divider />
                      <MenuItem
                        onClick={() => {
                          handleRemoveScenarioFromRunbook(scenarioMenuScenario.id);
                        }}
                      >
                        <ListItemIcon><RemoveCircleOutlineIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Remove from runbook</ListItemText>
                      </MenuItem>
                    </>
                  )}
                </Menu>
              </Box>
            )}
          </Box>
        ) : (
          <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
            Runbooks
          </Typography>

          {!selectedTenant?.tenant_id && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Select a tenant to view and manage runbooks.
            </Alert>
          )}

          {loading && <LinearProgress sx={{ mb: 3 }} />}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Runbook list</Typography>
            {canManage && selectedTenant?.tenant_id && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                Create runbook
              </Button>
            )}
          </Box>

          <TableContainer component={Paper} sx={{ overflowX: 'hidden' }}>
            <Table sx={{ tableLayout: 'fixed', width: '100%', '& .MuiTableCell-root': { fontSize: '1rem', py: 2, px: 1 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '8%' }}>Order</TableCell>
                  <TableCell sx={{ width: '30%' }}>Name</TableCell>
                  <TableCell sx={{ width: '38%' }}>Timeframe</TableCell>
                  <TableCell sx={{ width: '14%' }}>Max duration</TableCell>
                  <TableCell align="right" sx={{ width: '10%' }}>Scenarios</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runbooks.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                        {canManage
                          ? 'No runbooks yet. Create one to get started.'
                          : 'No runbooks in this tenant.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {runbooks.map((rb, index) => {
                  const showLineBefore = canManage && draggingRunbookId && dragOverRunbookId === rb.id && dragOverRunbookPosition === 'before';
                  const showLineAfter = canManage && draggingRunbookId && dragOverRunbookId === rb.id && dragOverRunbookPosition === 'after';
                  const dropLineSx = {
                    padding: 0,
                    minHeight: 2,
                    lineHeight: 0,
                    borderTop: '3px solid',
                    borderTopColor: theme.palette.primary.main,
                  };
                  const dropLineAfterSx = {
                    ...dropLineSx,
                    borderTop: 'none',
                    borderBottom: '3px solid',
                    borderBottomColor: theme.palette.primary.main,
                  };
                  const setRunbookDropPosition = (e: React.DragEvent, id: string, position?: 'before' | 'after') => {
                    e.preventDefault();
                    if (position != null) {
                      setDragOverRunbookId(id);
                      setDragOverRunbookPosition(position);
                    } else {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const mid = rect.top + rect.height / 2;
                      setDragOverRunbookId(id);
                      setDragOverRunbookPosition(e.clientY < mid ? 'before' : 'after');
                    }
                  };
                  return (
                    <React.Fragment key={rb.id}>
                      {showLineBefore && (
                        <TableRow key={`drop-before-${rb.id}`} onDragOver={(e: React.DragEvent) => setRunbookDropPosition(e, rb.id, 'before')} sx={{ '& > td': dropLineSx }}>
                          <TableCell colSpan={5} />
                        </TableRow>
                      )}
                    <TableRow
                      key={rb.id}
                      hover
                      onClick={() => handleOpenDetail(rb)}
                      sx={{ cursor: 'pointer' }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleOpenDetail(rb);
                        }
                      }}
                      {...(canManage && {
                        onDragOver: (e: React.DragEvent) => setRunbookDropPosition(e, rb.id),
                        onDragLeave: () => {
                          if (dragOverRunbookId === rb.id) {
                            setDragOverRunbookId(null);
                            setDragOverRunbookPosition(null);
                          }
                        },
                        onDrop: (e: React.DragEvent) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const sourceId = e.dataTransfer.getData('text/plain');
                          const pos = dragOverRunbookPosition ?? 'before';
                          if (sourceId) handleRunbookDrop(sourceId, rb.id, pos);
                          setDragOverRunbookId(null);
                          setDragOverRunbookPosition(null);
                          setDraggingRunbookId(null);
                        },
                      })}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()} sx={{ cursor: 'default' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ minWidth: 20 }}>{index + 1}</Typography>
                          {canManage && (
                            <Box
                              component="span"
                              draggable
                              onDragStart={(e: React.DragEvent) => {
                                e.dataTransfer.setData('text/plain', rb.id);
                                e.dataTransfer.effectAllowed = 'move';
                                setDraggingRunbookId(rb.id);
                                setDragOverRunbookId(null);
                                setDragOverRunbookPosition(null);
                              }}
                              onDragEnd={() => {
                                setDraggingRunbookId(null);
                                setDragOverRunbookId(null);
                                setDragOverRunbookPosition(null);
                              }}
                              sx={{
                                cursor: reordering ? 'default' : draggingRunbookId === rb.id ? 'grabbing' : 'grab',
                                opacity: reordering ? 0.5 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 28,
                                height: 28,
                                borderRadius: 1,
                                color: theme.palette.primary.main,
                                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                              }}
                              title="Drag to reorder"
                              aria-label="Drag to reorder runbook"
                            >
                              <DragIndicatorIcon sx={{ fontSize: '1.2rem' }} />
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{rb.name}</TableCell>
                      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatTimeframeRange(rb.timeframe_start, rb.timeframe_end, timezone)}</TableCell>
                      <TableCell>
                        {rb.max_duration_minutes != null ? `${rb.max_duration_minutes} min` : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip label={rb.scenario_count} size="small" />
                          {canManage && (
                            <Tooltip title="Duplicate runbook">
                              <IconButton
                                size="small"
                                aria-label="Duplicate runbook"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDuplicateRunbook(rb);
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                      {showLineAfter && (
                        <TableRow key={`drop-after-${rb.id}`} onDragOver={(e: React.DragEvent) => setRunbookDropPosition(e, rb.id, 'after')} sx={{ '& > td': dropLineAfterSx }}>
                          <TableCell colSpan={5} />
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        )}

        {/* Create/Edit runbook dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{editingRunbook ? 'Edit runbook' : 'Create runbook'}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Name"
              required
              fullWidth
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="Timeframe start"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.timeframe_start}
              onChange={(e) => setForm((f) => ({ ...f, timeframe_start: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="Timeframe end"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.timeframe_end}
              onChange={(e) => setForm((f) => ({ ...f, timeframe_end: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="Max duration (minutes)"
              type="number"
              fullWidth
              inputProps={{ min: 1 }}
              value={form.max_duration_minutes}
              onChange={(e) => setForm((f) => ({ ...f, max_duration_minutes: e.target.value }))}
            />
            <Box sx={{ mt: 1 }}>
              <Autocomplete
                freeSolo
                options={slackChannels.map((c) => `#${c.name}`)}
                value={form.slack_channel}
                onChange={(_, newValue) => setForm((f) => ({ ...f, slack_channel: (newValue ?? '') as string }))}
                onInputChange={(_, newValue) => setForm((f) => ({ ...f, slack_channel: newValue ?? '' }))}
                disabled={!isAdmin}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label="Slack channel"
                    fullWidth
                    placeholder={isAdmin ? '#channel-name or channel id' : ''}
                    helperText={
                      !isAdmin
                        ? 'Only super admins can change the Slack channel.'
                        : slackChannelsLoaded && slackConfigured === false
                          ? 'Slack is not configured on this server (SLACK_BOT_TOKEN unset). You can still type a channel; posting will be a no-op.'
                          : slackChannelsLoaded && slackChannels.length === 0
                            ? 'The CaosBlitz bot is not a member of any channels yet. Invite it to a channel, then refresh.'
                            : 'Runbook execution logs will be posted to this channel. Leave empty to disable.'
                    }
                  />
                )}
              />
            </Box>
            {!editingRunbook && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Scenarios
                </Typography>
                <Autocomplete
                  multiple
                  options={scenarios}
                  getOptionLabel={(s) => s.name}
                  value={selectedScenarios}
                  onChange={(_, newValue) => {
                    setForm((f) => ({ ...f, scenario_ids: newValue.map((s) => s.id) }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Select scenarios" size="small" />}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                      {option.attack_type && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({option.attack_type.name})
                        </Typography>
                      )}
                    </li>
                  )}
                />
                {canManage && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setCreateScenarioContext('runbookCreate');
                      setEditingScenario(null);
                      setCreateScenarioOpen(true);
                    }}
                    sx={{ mt: 1 }}
                  >
                    Add new scenario
                  </Button>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              {editingRunbook ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add/remove scenarios dialog */}
        <Dialog open={addScenariosOpen} onClose={() => setAddScenariosOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add/remove scenarios</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 0 }}>
              Scenarios in this runbook
            </Typography>
            {addRemoveScenarioIds.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No scenarios. Add some below.
              </Typography>
            ) : (
              <Box sx={{ mb: 2 }}>
                {addRemoveScenarioIds.map((id) => {
                  const scenario = scenarios.find((s) => s.id === id) ?? detailRunbook?.scenarios.find((s) => s.id === id);
                  const name = scenario?.name ?? id;
                  return (
                    <Box
                      key={id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 0.5,
                        px: 1,
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Typography variant="body2">{name}</Typography>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={`Remove ${name}`}
                        onClick={() => setAddRemoveScenarioIds((prev) => prev.filter((x) => x !== id))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            )}
            <Typography variant="subtitle2" gutterBottom>
              Add scenarios
            </Typography>
            <Autocomplete
              multiple
              options={scenarios.filter((s) => !addRemoveScenarioIds.includes(s.id))}
              getOptionLabel={(s) => s.name}
              value={scenarios.filter((s) => addPickerSelection.includes(s.id))}
              onChange={(_, newValue) => {
                const ids = newValue.map((s) => s.id);
                setAddRemoveScenarioIds((prev) => Array.from(new Set([...prev, ...ids])));
                setAddPickerSelection([]);
              }}
              renderInput={(params) => <TextField {...params} label="Select scenarios to add" size="small" />}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                  {option.attack_type && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({option.attack_type.name})
                    </Typography>
                  )}
                </li>
              )}
            />
            {canManage && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCreateScenarioContext('addRemoveDialog');
                  setEditingScenario(null);
                  setCreateScenarioOpen(true);
                }}
                sx={{ mt: 1 }}
              >
                Create new scenario
              </Button>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddScenariosOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleApplyAddRemoveScenarios}
              disabled={addingScenarios}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete runbook</DialogTitle>
          <DialogContent>
            {runbookToDelete && (
              <Typography>
                Are you sure you want to delete runbook &quot;{runbookToDelete.name}&quot;? This does not delete the
                scenarios.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleting}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create/Edit scenario */}
        <CreateScenarioDialog
          open={createScenarioOpen}
          onClose={() => {
            setCreateScenarioOpen(false);
            setEditingScenario(null);
            setCreateScenarioContext(null);
          }}
          onSuccess={handleCreateScenarioSuccess}
          mode={editingScenario ? 'edit' : 'create'}
          scenarioToEdit={editingScenario}
        />

        {/* Run Debug — single bot + in-UI terminal for a runbook scenario */}
        <ScenarioDebugDialog
          open={Boolean(debugScenario)}
          scenario={debugScenario}
          onClose={() => setDebugScenario(null)}
        />

        {/* Confirm terminate current scenario and activate another */}
        <Dialog
          open={activateConfirm !== null}
          onClose={handleActivateConfirmClose}
          aria-labelledby="activate-confirm-title"
          aria-describedby="activate-confirm-description"
        >
          <DialogTitle id="activate-confirm-title">
            Terminate current scenario?
          </DialogTitle>
          <DialogContent>
            <Typography id="activate-confirm-description">
              {currentScenario && activateConfirm && (
                <>
                  Scenario &quot;{currentScenario.name}&quot; is currently active. Do you want to terminate it and activate &quot;{activateConfirm.scenarioName}&quot;?
                </>
              )}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleActivateConfirmClose} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleActivateConfirm} color="primary" variant="contained" autoFocus>
              Terminate and activate
            </Button>
          </DialogActions>
        </Dialog>
      </PageTransition>

    </PermissionGate>
  );
};

export default Runbooks;
